<template>
  <div>
    <div class="document-container">
      <div class="modal is-active"
        role="dialog"
        :aria-label="'Confirm Decision'"
        aria-hidden="false"
        v-if="showModal"
      >
        <div class="modal-background"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Confirm Decision
            </p>
            <button
              class="icon is-right"
              @click.prevent="toggleModal()"
            >
              <font-awesome-icon :icon="['fal', 'times']" title="Close"/>
            </button>
          </header>
          <section class="modal-card-body">
            <div class="modal-body">
              Are you sure you want to refresh population data for every SFA?
            </div>
            <div class="update-box">
              <button @click.prevent="toggleModal()"
                class="button button-link has-underline"
              >
                Cancel
              </button>
              <button
                class="button is-primary"
                @click="startPopulationRefresh()"
              >
                Yes, Refresh
              </button>
            </div>
          </section>
        </div>
      </div>
      <div class="document">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                CEP MANAGER
              </div>
            </template>
            <template #actions
              v-if="isStateUser
                && hasModule('DRMCH:CEP:POPULATION:REFRESH')
                && isPopulationWindowOpen"
            >
              <div class="is-flex flex-align--center">
                <div class="header-actions-right">
                  <span v-if="refreshingPopulationData" class="header-loader-center">
                    <es-loader size="3x"/>
                  </span>
                  <span v-else class="last-pop-refresh-info">
                    <div class="last-pop-refresh-label">
                      Last Population Refresh:
                    </div>
                    <div>
                        {{ lastPopulationRefreshInfo }}
                    </div>
                  </span>
                  <span class="refresh-btn-separator"></span>
                  <button
                    class="button is-primary"
                    @click="toggleModal"
                    :disabled="!canStartNewProcess
                      || refreshingPopulationData"
                  >
                    Refresh Population Data
                  </button>
                </div>
              </div>
            </template>
          </es-header>
        </div>
        <div class="tabs-container">
          <div class="tabs no-bottom-border">
            <ul>
              <li :class="{ 'is-active': cepTab === 'population' }">
                <router-link
                  class="is-uppercase"
                  :to="{
                    path: '/directMatch/cep/population',
                    query: {
                      locationGroupId: locationGroupId,
                      schoolYear: currentSchoolYear,
                    },
                  }"
                >
                  population
                </router-link>
              </li>
              <li :class="{
                  'is-active': cepTab === 'electionsRecalculate',
                  'disabled': !electionsPermissions.canAccess
                  || !this.$store.getters.permissions.includes('DRMCH:CEP:ELECTIONS:RECALCULATE')
                }">
                <router-link
                  class="is-uppercase"
                  :to="{
                    path: '/directMatch/cep/electionsRecalculate',
                    query: {
                      locationGroupId: locationGroupId,
                      schoolYear: currentSchoolYear,
                    },
                  }"
                >
                  elections - recalculate
                </router-link>
              </li>
              <li :class="{ 'is-active': cepTab === 'electionsSubmission' }">
                <router-link
                  class="is-uppercase"
                  :to="{
                    path: '/directMatch/cep/electionsSubmission',
                    query: {
                      locationGroupId: locationGroupId,
                      schoolYear: currentSchoolYear,
                    },
                  }"
                >
                  elections - submission
                </router-link>
              </li>
              <li v-if="isLEAListUser" :class="{ 'is-active': cepTab === 'lealist' }">
                <router-link
                  class="is-uppercase"
                  :to="{
                    path: '/directMatch/cep/lealist',
                    query: {
                      schoolYear: currentSchoolYear,
                    },
                  }"
                >
                  LEA LIST
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapGetters } from 'vuex';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    PermissionManager,
  ],
  components: {
    EsHeader,
    EsLoader,
  },
  props: {
    cepTab: {
      type: String,
    },
    electionsPermissions: {
      type: Object,
    },
    locationGroupId: {
      type: [String, Number],
    },
  },
  data() {
    return {
      canStartNewProcess: false,
      lastPopulationRefreshInfo: null,
      refreshingPopulationData: false,
      showModal: false,
    };
  },
  mounted() {
    if (this.isPopulationWindowOpen) {
      this.fetchLastPopulationRefresh();
    }
  },
  watch: {
    cepTab(newVal) {
      // If user navigates back to this tab, refresh the info
      if (newVal === 'population') {
        this.fetchLastPopulationRefresh();
      }
    },
  },
  computed: {
    ...mapGetters({
      isLEAListUser: 'CEPLEAListUser',
    }),
    ...mapGetters([
      'isStateUser',
    ]),
    currentSchoolYear() {
      return this.properties['CEP-CEP_CURRENT_SCHOOL_YEAR'];
    },
    isPopulationWindowOpen() {
      const open = this.populationSubmissionOpenDate;
      const cutoff = this.populationCutoffDate;
      if (!open || !cutoff) return false;

      const today = moment();
      const openDate = moment(open);
      const cutoffDate = moment(cutoff);

      if (!openDate.isValid() || !cutoffDate.isValid()) return false;

      return today.isBetween(openDate, cutoffDate, 'day', '[]');
    },
    properties() {
      return this.$store.state.propertiesModule.properties;
    },
    populationCutoffDate() {
      return this.properties['CEP-POPULATION_CUTOFF_DATE'];
    },
    populationSubmissionOpenDate() {
      return this.properties['CEP-POPULATION_SUBMISSION_OPEN_DATE'];
    },
  },
  methods: {
    async fetchLastPopulationRefresh() {
      await this.handlePopulationRefresh(
        () => axios.get('/cep/population/refresh-all/status'),
        'Failed to fetch population refresh info:',
      );
    },
    formatDateMMDDYYYYHHMM(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY hh:mm A');
      }
      return 'N/A';
    },
    async handlePopulationRefresh(apiCall, errorMsg) {
      this.refreshingPopulationData = true;
      try {
        const { data, status } = await apiCall();
        if (status === 200 && data) {
          const {
            lastRefreshUserName,
            lastRefreshAllStartDate,
            canStartNewProcess,
          } = data;
          if (!lastRefreshUserName || !lastRefreshAllStartDate) {
            this.lastPopulationRefreshInfo = 'N/A';
          } else {
            this.lastPopulationRefreshInfo = `${lastRefreshUserName} ${this.formatDateMMDDYYYYHHMM(lastRefreshAllStartDate)}`;
          }
          this.canStartNewProcess = !!canStartNewProcess;
        }
      } catch (error) {
        console.error(errorMsg, error);
      } finally {
        this.refreshingPopulationData = false;
      }
    },
    async startNewPopulationRefresh() {
      await this.handlePopulationRefresh(
        () => axios.post('/cep/population/refresh-all'),
        'Failed to start new population refresh:',
      );
    },
    startPopulationRefresh() {
      this.startNewPopulationRefresh();
      this.toggleModal();
    },
    toggleModal() {
      this.showModal = !this.showModal;
    },
  },
};
</script>

<style lang="sass" scoped>
  .header-actions-right
    display: flex
    align-items: center
    justify-content: flex-end
    width: 100%
  .header-loader-center
    display: flex
    align-items: center
    justify-content: flex-end
    padding-right: 6rem
    width: 100%
  .last-pop-refresh-info
    padding-right: 1rem
    font-size: 12px
  .last-pop-refresh-label
    color: #737373
  .modal-body
    margin-bottom: 2rem
    display: flex
    justify-content: flex-start
  .modal-card-body
    border-bottom-right-radius: .4rem
    border-bottom-left-radius: .4rem
    text-align: left
  .modal-card-head
    background-color: #F5F5F5
    text-align: left
  .modal-card-title
    margin-bottom: 0 !important
  .refresh-btn-separator
    display: inline-block
    border-left: 1px solid #dee2e6
    height: 2.2rem
    margin-right: 1rem
  .tabs-container .tabs ul li.disabled
    pointer-events: none
    cursor: not-allowed
    a, a.is-active
      color: #999
  .update-box
    display: flex
    justify-content: flex-end
    gap: 0.5rem;
</style>
